import { MasterData } from './master-data';
import { Storage } from 'src/core/storage/domain/storage';
import { ServiceAddress, ServiceAddressPrimitives } from './service-address';
import { Ambit } from './ambit';
import { Injectable } from '@angular/core';
import { StorableService } from 'src/core/storage/domain/storable-service';
import { Initializable } from 'src/core/common/domain/initializer/initializable';
import { BehaviorSubject, Observable } from 'rxjs';
import { CloseTaskActions, CloseTaskActionsData } from './close-task-actions';
import { Nullable } from 'src/core/common/domain/types/types';
import { Assignee, FilterDate, Filters } from './filters';
import { WorkType } from './work-type';
import { DefaultFilters } from './default-filters';
import { LocalDate } from 'src/core/common/domain/date/local-date';
import { TenantTimezone } from 'src/core/common/domain/date/tenant-timezone';
import { TasksFilters } from 'src/core/filters/domain/filters-service';
import {
  MasterDataConfiguration,
  MasterDataConfigurationData,
  UserPresenceConfiguration,
} from './master-data-configuration';
import { RejectTaskActions, RejectTaskActionsData } from './reject-task-actions';
import { TaskActions } from './task-actions';
import { ConfirmCloseTaskActions, ConfirmCloseTaskActionsData } from './confirm-close-task-actions';
import { ConfirmRejectTaskActions, ConfirmRejectTaskActionsData } from './confirm-reject-task-actions';
import { DateRange } from './date-range';
import { Contact } from './contact';
import { Task } from './task';

export type DateFilter = {
  date: { id: string; from: string; to: string };
  customRange: { from: string; to: string };
};

@Injectable({
  providedIn: 'root',
})
export class MasterDataService implements StorableService, Initializable {
  private readySubject = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  ready$: Observable<boolean> = this.readySubject.asObservable();
  private readonly KEY_MASTER_DATA_TIMESTAMP = 'master_data.timestamp';
  private readonly KEY_ERROR_ON_LAST_DOWNLOAD = 'master_data.error_on_download';
  private readonly KEY_IS_LOADED = 'master_data.is_loaded';
  private readonly KEY_FILTERS = 'master_data.filters';
  private readonly KEY_AVAILABLE_ACTIONS = 'master_data.available_actions';
  private readonly KEY_SERVICE_ADDRESSES = 'master_data.service_addresses';
  private readonly KEY_AMBIT_LIST = 'master_data.ambit_list';
  private readonly KEY_CLOSE_TASK_ACTIONS = 'master_data.close_task_actions';
  private readonly KEY_CONFIRM_CLOSE_TASK_ACTIONS = 'master_data.confirm_close_task_actions';
  private readonly KEY_REJECT_TASK_ACTIONS = 'master_data.reject_task_actions';
  private readonly KEY_CONFIRM_REJECT_TASK_ACTIONS = 'master_data.confirm_reject_task_actions';
  private readonly KEY_CONFIGURATION = 'master_data.configuration';
  private readonly userPresenceConfigurationSubject: BehaviorSubject<UserPresenceConfiguration> = new BehaviorSubject<
    Nullable<UserPresenceConfiguration>
  >(this.getUserPresenceConfiguration());
  readonly userPresenceConfiguration$: Observable<UserPresenceConfiguration> =
    this.userPresenceConfigurationSubject.asObservable();

  private isLoaded = false;
  private timestamp: Nullable<string> = null;
  private masterData: Nullable<MasterData> = null;
  private filters: Nullable<Filters> = null;
  private availableActions: Nullable<Array<TaskActions>> = null;
  private serviceAddresses: Nullable<Array<ServiceAddress>> = null;
  private ambitList: Nullable<Array<Ambit>> = null;
  private closeTaskActions: Nullable<CloseTaskActions> = null;
  private confirmCloseTaskActions: Nullable<ConfirmCloseTaskActions> = null;
  private rejectTaskActions: Nullable<RejectTaskActions> = null;
  private confirmRejectTaskActions: Nullable<ConfirmRejectTaskActions> = null;
  private configuration: Nullable<MasterDataConfiguration> = null;
  private lastDownloadCausedError = false;

  constructor(private storage: Storage) {}

  async init(): Promise<void> {
    await this.initFromStorage();

    this.readySubject.next(true);
  }

  async clear(): Promise<void> {
    await this.storage.remove(this.KEY_IS_LOADED);
    await this.storage.remove(this.KEY_ERROR_ON_LAST_DOWNLOAD);
    await this.storage.remove(this.KEY_MASTER_DATA_TIMESTAMP);
    await this.storage.remove(this.KEY_FILTERS);
    await this.storage.remove(this.KEY_AVAILABLE_ACTIONS);
    await this.storage.remove(this.KEY_SERVICE_ADDRESSES);
    await this.storage.remove(this.KEY_AMBIT_LIST);
    await this.storage.remove(this.KEY_CLOSE_TASK_ACTIONS);
    await this.storage.remove(this.KEY_CONFIRM_CLOSE_TASK_ACTIONS);
    await this.storage.remove(this.KEY_REJECT_TASK_ACTIONS);
    await this.storage.remove(this.KEY_CONFIRM_REJECT_TASK_ACTIONS);
    await this.storage.remove(this.KEY_CONFIGURATION);
    this.isLoaded = false;
    this.timestamp = null;
    this.filters = null;
    this.availableActions = null;
    this.serviceAddresses = null;
    this.ambitList = null;
    this.closeTaskActions = null;
    this.confirmCloseTaskActions = null;
    this.rejectTaskActions = null;
    this.confirmRejectTaskActions = null;
    this.configuration = null;
    this.masterData = null;
    // Presence control would otherwise go on with the previous user's settings until the next download.
    this.userPresenceConfigurationSubject.next(null);
    TenantTimezone.reset();
  }

  get(): MasterData {
    return this.masterData;
  }

  getTimestamp(): Nullable<string> {
    return this.timestamp;
  }

  getLastDownloadCausedError(): boolean {
    return this.lastDownloadCausedError;
  }

  async save(masterData: MasterData): Promise<void> {
    // Applied before the timestamp below, so it is already stamped in the tenant's zone.
    this.applyTenantTimezone(masterData.configuration);
    const timestamp = LocalDate.now().atom;
    await this.storage.set(this.KEY_FILTERS, masterData.filters.toPrimitives());
    await this.storage.set(this.KEY_AVAILABLE_ACTIONS, masterData.availableActions);
    await this.storage.set(
      this.KEY_SERVICE_ADDRESSES,
      masterData.service_addresses.map((s) => s.toPrimitives())
    );
    await this.storage.set(this.KEY_AMBIT_LIST, masterData.ambitList);
    await this.storage.set(this.KEY_CLOSE_TASK_ACTIONS, masterData.closeTaskActions.value);
    await this.storage.set(this.KEY_CONFIRM_CLOSE_TASK_ACTIONS, masterData.confirmCloseTaskActions.value);
    await this.storage.set(this.KEY_REJECT_TASK_ACTIONS, masterData.rejectTaskActions.value);
    await this.storage.set(this.KEY_CONFIRM_REJECT_TASK_ACTIONS, masterData.confirmRejectTaskActions.value);
    await this.storage.set(this.KEY_CONFIGURATION, masterData.configuration.value);
    await this.storage.set(this.KEY_ERROR_ON_LAST_DOWNLOAD, masterData.lastDownloadCausedError);
    await this.storage.set(this.KEY_MASTER_DATA_TIMESTAMP, timestamp);
    await this.storage.set(this.KEY_IS_LOADED, true);

    const presenceConfiguration = masterData?.configuration?.getPresenceConfiguration() ?? null;
    this.userPresenceConfigurationSubject.next(presenceConfiguration);

    this.filters = masterData.filters;
    this.availableActions = masterData.availableActions;
    this.serviceAddresses = masterData.service_addresses;
    this.ambitList = masterData.ambitList;
    this.closeTaskActions = masterData.closeTaskActions;
    this.confirmCloseTaskActions = masterData.confirmCloseTaskActions;
    this.rejectTaskActions = masterData.rejectTaskActions;
    this.confirmRejectTaskActions = masterData.confirmRejectTaskActions;
    this.configuration = masterData.configuration;
    this.lastDownloadCausedError = masterData.lastDownloadCausedError;
    this.timestamp = timestamp;
    this.masterData = masterData;
    this.isLoaded = true;
  }

  getUserPresenceConfiguration(): Nullable<UserPresenceConfiguration> {
    return this.masterData?.configuration?.getPresenceConfiguration() ?? null;
  }

  private applyTenantTimezone(configuration: Nullable<MasterDataConfiguration>): void {
    TenantTimezone.set(configuration?.getTimezone() ?? null);
  }

  private async updateServiceAddress(serviceAddress: ServiceAddress) {
    // in memory
    const index = this.serviceAddresses.findIndex((s) => s.id === serviceAddress.id);
    if (index !== -1) {
      this.serviceAddresses[index] = serviceAddress;
    }

    // stored
    const serviceAddressesPrimitives: ServiceAddressPrimitives[] = await this.storage.get(this.KEY_SERVICE_ADDRESSES);
    const indexPrimitives = serviceAddressesPrimitives.findIndex(
      (s: ServiceAddressPrimitives) => s.id === serviceAddress.id
    );
    if (indexPrimitives !== -1) {
      serviceAddressesPrimitives[index] = serviceAddress.toPrimitives();
      await this.storage.set(this.KEY_SERVICE_ADDRESSES, serviceAddressesPrimitives);
    }
  }

  getDefaultFilters(): Nullable<DefaultFilters> {
    if (!this.filters || !this.filters.defaultFilters) {
      return null;
    }

    return this.filters.defaultFilters;
  }

  calculateDateFilterForDateId(dateId: string, currentCustomRange: DateRange): DateFilter {
    // get structure for DateFilter: date { id, from, to }, and customRange { from, to }
    // ensuring that if dateId is not available, it is recalculated to an available date and customRange.

    const maxDaysRange = this.filters.getMaxDaysRange();
    let rangeForDateId = this.getDateRangeForDateOption(dateId, currentCustomRange);
    // let defaultDateRangeForDateId = this.getDefaultDateRangeForDateOption(dateId, customRange, maxDaysRange);

    if (!currentCustomRange.fulfillsMaxDaysRange(maxDaysRange)) {
      currentCustomRange = DateRange.aroundToday(maxDaysRange);
    }

    let defaultDateRangeForDateId = currentCustomRange;

    if (!rangeForDateId.fulfillsMaxDaysRange(maxDaysRange)) {
      dateId = FilterDate.TODAY;
      currentCustomRange = DateRange.aroundToday(maxDaysRange);
      rangeForDateId = this.getDateRangeForDateOption(dateId, currentCustomRange);
      defaultDateRangeForDateId = this.getDefaultDateRangeForDateOption(dateId, currentCustomRange, maxDaysRange);
    }

    // This date option does not have a "preset", so we transform it to a generic "range"
    if (dateId === FilterDate.RANGE_SEVEN_DAYS_BEFORE_AND_AFTER) {
      dateId = FilterDate.RANGE;
    }

    return {
      date: {
        id: dateId,
        from: rangeForDateId.from.ymd,
        to: rangeForDateId.to.ymd,
      },
      customRange: {
        from: defaultDateRangeForDateId.from.ymd,
        to: defaultDateRangeForDateId.to.ymd,
      },
    };
  }

  getDefaultTasksFilters(): Nullable<TasksFilters> {
    const masterDataDefaultFilters = this.getDefaultFilters();
    if (masterDataDefaultFilters === null) {
      return null;
    }

    const dateFilter = this.calculateDateFilterForDateId(
      masterDataDefaultFilters.date,
      DateRange.sevenDaysBeforeAndAfterRange()
    );

    return {
      workType: masterDataDefaultFilters.workTypes,
      status: masterDataDefaultFilters.statuses,
      assigned: masterDataDefaultFilters.assignee,
      priority: masterDataDefaultFilters.priorities.map((priorityId) => String(priorityId)),
      route: masterDataDefaultFilters.routes,
      date: dateFilter.date,
      customRange: dateFilter.customRange,
      deadline: [], // TODO <-- Think where should be calculated
    };
  }

  getDateRangeForDateOption(dateId: string, range: DateRange): DateRange {
    switch (dateId) {
      case FilterDate.THIS_WEEK:
        return DateRange.thisWeekRange();
      case FilterDate.THIS_MONTH:
        return DateRange.thisMonthRange();
      case FilterDate.LAST_MONTH:
        return DateRange.lastMonthRange();
      case FilterDate.RANGE:
        return range;
      case FilterDate.RANGE_SEVEN_DAYS_BEFORE_AND_AFTER:
        return DateRange.sevenDaysBeforeAndAfterRange();
      case FilterDate.RANGE_THREE_MONTHS_BEFORE_AND_AFTER:
        return DateRange.threeMonthsBeforeAndAfterRange();
      default:
      case FilterDate.TODAY:
        return DateRange.today();
    }
  }

  getDefaultDateRangeForDateOption(dateId: string, range: DateRange, maxDaysRange: number): DateRange {
    let customRange: DateRange;

    // The default date range uses this logic
    if (dateId === FilterDate.RANGE_SEVEN_DAYS_BEFORE_AND_AFTER) {
      customRange = DateRange.sevenDaysBeforeAndAfterRange();
    } else if (dateId === FilterDate.RANGE) {
      customRange = range;
    } else {
      if (maxDaysRange >= 90) {
        customRange = DateRange.oneMonthBeforeAndAfter();
      } else {
        customRange = DateRange.aroundToday(maxDaysRange);
      }
    }

    // ensure it does not exceed the max_days_range
    if (!customRange.fulfillsMaxDaysRange(maxDaysRange)) {
      customRange = DateRange.aroundToday(maxDaysRange);
    }

    return customRange;
  }

  getCloseTaskActions(): Nullable<CloseTaskActions> {
    return this.closeTaskActions;
  }

  getConfirmCloseTaskActions(): Nullable<ConfirmCloseTaskActions> {
    return this.confirmCloseTaskActions;
  }

  getConfiguration(): Nullable<MasterDataConfiguration> {
    return this.configuration;
  }

  getFilters(): Nullable<Filters> {
    return this.filters;
  }

  findWorkType(id: string): Nullable<WorkType> {
    return this.filters?.findWorkType(id) ?? null;
  }

  getAmbitList(): Nullable<Array<Ambit>> {
    return this.ambitList;
  }

  findAmbitsByCode(code: string): Array<Ambit> {
    const ambits = (this.ambitList ?? []).filter((ambit) => ambit.code === code);
    return ambits;
  }

  /**
   * What the API grants on a task. The most specific level that informs it wins, so the top-level actions a
   * backend may keep sending for older app versions never override a service address or a task. An empty list
   * counts as informed: it grants nothing and does not fall through to the next level.
   */
  grantedActionsFor(task: Task): Array<TaskActions> {
    return (
      task.availableActions ??
      this.getServiceAddressById(task.serviceAddressId())?.availableActions ??
      this.availableActions ??
      []
    );
  }

  getServiceAddresses(): Nullable<Array<ServiceAddress>> {
    return this.serviceAddresses;
  }

  getServiceAddressById(id: string): Nullable<ServiceAddress> {
    // cover the case of milliseconds after logout where master data is deleted
    return this.serviceAddresses?.find((sa) => sa.id === id) ?? null;
  }

  isDataDownloaded() {
    return this.isLoaded;
  }

  getRejectTaskActions(): Nullable<RejectTaskActions> {
    return this.rejectTaskActions;
  }

  getConfirmRejectTaskActions(): Nullable<ConfirmRejectTaskActions> {
    return this.confirmRejectTaskActions;
  }

  async markErrorOnDownload() {
    this.lastDownloadCausedError = true;
    await this.storage.set(this.KEY_ERROR_ON_LAST_DOWNLOAD, this.lastDownloadCausedError);
  }

  defaultAssigneeHasMe(): boolean {
    const defaultFilters = this.getDefaultFilters();
    if (!defaultFilters) {
      return false;
    }

    if (defaultFilters.assignee.length > 0 && defaultFilters.assignee.find((a) => a === Assignee.ME)) {
      return true;
    }

    return false;
  }

  private async initFromStorage(): Promise<void> {
    const filters = await this.storage.get(this.KEY_FILTERS);
    this.filters = filters ? Filters.fromPrimitives(filters) : null;
    this.availableActions = await this.storage.get(this.KEY_AVAILABLE_ACTIONS);
    const serviceAddresses = (await this.storage.get(this.KEY_SERVICE_ADDRESSES)) ?? [];
    this.serviceAddresses = serviceAddresses.map((s: ServiceAddressPrimitives) => ServiceAddress.fromPrimitives(s));
    this.ambitList = await this.storage.get(this.KEY_AMBIT_LIST);
    const closeTaskActions = await this.storage.get(this.KEY_CLOSE_TASK_ACTIONS);
    this.closeTaskActions = new CloseTaskActions(closeTaskActions as CloseTaskActionsData);
    const confirmCloseTaskActions = await this.storage.get(this.KEY_CONFIRM_CLOSE_TASK_ACTIONS);
    this.confirmCloseTaskActions = new ConfirmCloseTaskActions(confirmCloseTaskActions as ConfirmCloseTaskActionsData);
    const rejectTaskActions = await this.storage.get(this.KEY_REJECT_TASK_ACTIONS);
    this.rejectTaskActions = new RejectTaskActions(rejectTaskActions as RejectTaskActionsData);
    const confirmRejectTaskActions = await this.storage.get(this.KEY_CONFIRM_REJECT_TASK_ACTIONS);
    this.confirmRejectTaskActions = new ConfirmRejectTaskActions(
      confirmRejectTaskActions as ConfirmRejectTaskActionsData
    );
    const configurationData = await this.storage.get(this.KEY_CONFIGURATION);
    this.configuration = new MasterDataConfiguration(configurationData as MasterDataConfigurationData);
    this.applyTenantTimezone(this.configuration);
    this.timestamp = (await this.storage.get(this.KEY_MASTER_DATA_TIMESTAMP)) ?? null;
    this.lastDownloadCausedError = await this.storage.get(this.KEY_ERROR_ON_LAST_DOWNLOAD);
    this.isLoaded = await this.storage.get(this.KEY_IS_LOADED);

    this.masterData = new MasterData(
      this.serviceAddresses,
      this.filters,
      this.ambitList,
      [],
      this.availableActions,
      this.closeTaskActions,
      this.confirmCloseTaskActions,
      this.rejectTaskActions,
      this.confirmRejectTaskActions,
      this.configuration,
      this.lastDownloadCausedError
    );

    const userPresenceConfiguration = this.masterData.configuration.getPresenceConfiguration();
    this.userPresenceConfigurationSubject.next(userPresenceConfiguration);
  }

  contactsForServiceAddress(serviceAddressId: string): Array<Contact> {
    const serviceAddress = this.serviceAddresses.find((sa) => sa.id === serviceAddressId) ?? null;
    if (!serviceAddress) {
      return [];
    }

    return serviceAddress.recipientEmails;
  }

  addContactsForServiceAddress(serviceAddressId: string, emails: Array<string>): void {
    const serviceAddress = this.serviceAddresses.find((sa) => sa.id === serviceAddressId) ?? null;
    if (!serviceAddress) {
      return;
    }

    const added = serviceAddress.addEmails(emails);
    if (added) {
      this.updateServiceAddress(serviceAddress);
    }
  }
}
